/* eslint-disable react-hooks/exhaustive-deps */
// TODO ADD LOADING
// Fix triple rerender
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { usePagination, useRowSelect, useSortBy, useTable } from 'react-table';

import './reactTable.style.scss';
import { v1 } from 'uuid';
import DisplayColumnIcon from 'components/icons/DisplayColumnIcon';
import EditIcon from 'components/icons/EditIcon';
import DeleteIcon from 'components/icons/DeleteIcon';
import Select from '../Select/Select';
import Button from '../Button/Button';

import { convertDateToUTC, getFromLocalStorage, saveToLocalStorage } from 'utils/func';
import { DAYS_MILLISECONDS, FLIGHT_STATUS_NAMES } from 'utils/const';
import useAuth from 'providers/Auth/useAuth';

const Table = ({ selectedRows, datas, headers, editable = false, onEdit, onDelete = null, extraActions = [], filters = true, selectable = true, name = 'default', initialSelectAll = false }) => {
  const user = useAuth();
  // CLOSE POPUP ON CLICK OUTSIDE
  const [filterPopupVisibility, setFilterPopupVisibility] = useState(false);
  const filterRef = useRef(null);
  const tableRef = useRef(null);

  // La colonne d'actions est sticky à droite. Selon la page, le conteneur qui
  // défile horizontalement est `.content` ou la page elle-même, avec un
  // padding droit différent : on l'injecte pour que la colonne reprenne
  // exactement sa place en fin de défilement (sans mordre sur la précédente).
  useEffect(() => {
    const updateActionsInset = () => {
      const tableEl = tableRef.current;
      if (!tableEl) return;
      let scroller = tableEl.parentElement;
      while (scroller) {
        const overflowX = getComputedStyle(scroller).overflowX;
        if (overflowX === 'auto' || overflowX === 'scroll') break;
        scroller = scroller.parentElement;
      }
      const paddingRight = scroller
        ? parseFloat(getComputedStyle(scroller).paddingRight) || 0
        : 0;
      tableEl.style.setProperty('--table-actions-right', `${-paddingRight}px`);
    };
    updateActionsInset();
    window.addEventListener('resize', updateActionsInset);
    return () => window.removeEventListener('resize', updateActionsInset);
  }, []);
  useEffect(() => {
    if (!filterPopupVisibility) return;
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setFilterPopupVisibility(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filterPopupVisibility]);

  // Gestion du popup Missions
  const [missionsPopupVisibility, setMissionsPopupVisibility] = useState(false);
  const missionsRef = useRef(null);
  useEffect(() => {
    if (!missionsPopupVisibility) return;
    const handleClickOutside = (event) => {
      if (missionsRef.current && !missionsRef.current.contains(event.target)) {
        setMissionsPopupVisibility(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [missionsPopupVisibility]);

  // Récupération des missions uniques
  const allMissions = useMemo(() => {
    return Array.from(new Set(datas.map(d => d.mission).filter(Boolean)));
  }, [datas]);

  // State des missions sélectionnées
  const [selectedMissions, setSelectedMissions] = useState(allMissions);
  useEffect(() => {
    setSelectedMissions(allMissions);
  }, [allMissions]);
  const [UTCDiff, setUTCDiff] = useState(false);

  // Filtrage des données selon missions sélectionnées
  const data = useMemo(() => {
    // Si aucune mission dans le dataset, on retourne tout
    if (!allMissions || allMissions.length === 0) return datas;
    // Si aucune mission sélectionnée, on retourne rien
    if (!selectedMissions || selectedMissions.length === 0) return [];
    // Sinon, on filtre
    return datas.filter(d => selectedMissions.includes(d.mission));
  }, [datas, selectedMissions, allMissions]);

  const columns = useMemo(() => headers, [headers]);

  const {
    getTableProps,
    getTableBodyProps,
    allColumns,
    headerGroups,
    selectedFlatRows,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    visibleColumns,
    setHiddenColumns,
    setPageSize,
    toggleAllRowsSelected,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 30, pageIndex: 0 },
    },
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      selectable &&
        hooks.visibleColumns.push(columns => [
          {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
              </div>
            ),

            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            ),
          },
          ...columns,
        ]);
    },
  );

  // Sélectionner toutes les lignes au montage si initialSelectAll est true
  useEffect(() => {
    if (initialSelectAll && data.length > 0) {
      toggleAllRowsSelected(true);
    }
  }, [initialSelectAll, data, toggleAllRowsSelected]);

  useEffect(() => {
    selectedRows(selectedFlatRows);
  }, [selectedFlatRows, selectedRows]);

  useEffect(() => {
    if (filters) {
      const headersId = headers.map(e => e.accessor);
      const storedHiddenColumns = getFromLocalStorage(name);
      storedHiddenColumns && setHiddenColumns(storedHiddenColumns.filter(value => headersId.includes(value)));
    } else {
      setHiddenColumns(['id']);
    }
    // TODO: END LOADING
  }, [headers]);

  useEffect(() => {
    setUTCDiff(((+convertDateToUTC(new Date()) % DAYS_MILLISECONDS) / 1000 / 60).toFixed(0));
  }, []);

  /**
   * Rules for react table rows
   * @param {Object} row react row prepared datas
   * @returns
   * SPECS :
   * - Vol qui a décollé : Ligne écrite en bleu
   * - Vol terminated : Ligne en gris
   * - Vol updated avant décollage (champs updatedAt != du champs createdAt) : Ligne en rouge
   * - Vol created : blanc
   */
  const applyRowClass = row => {
    const data = row.values.raw;
    if (data) {
      if (row.values && ['canceledFlightFilters', 'allFlightsFilters', 'flightsAnomaliesFilters', 'flightsFilters'].includes(name)) {
        const date = new Date();
        const splitedFlightDate = row.values.date.split('/');
        const flightDate = new Date(`${splitedFlightDate[1]}/${splitedFlightDate[0]}/${splitedFlightDate[2]}`);
        if(data?.flightStatus?.name === FLIGHT_STATUS_NAMES.PREVIEW) {
          return ''
        }
        if (data?.flightStatus?.name === FLIGHT_STATUS_NAMES.CANCELLED) {
          return 'cancelled-flight';
        } else {
          if (flightDate >= new Date(date.getFullYear() + '/' + (date.getMonth() + 1) + '/' + date.getDate())) {
            let takeOff = (row.values.takeoffTimeReal ? row.values.takeoffTimeReal?.split(':') : row.values.takeoffTimeRally?.split(':')).map(v => +v);
            let landing = row.values.landingTimeReal?.split(':');
            let flightToday =
              flightDate.getFullYear() === date.getFullYear() && flightDate.getMonth() + 1 === date.getMonth() + 1 && flightDate.getDate() === date.getDate();
            if (flightDate >= new Date(date.getFullYear() + '/' + (date.getMonth() + 1) + '/' + date.getDate())) {
              if (flightToday && (!landing || (landing[0] !== '--' && landing[1] !== '--'))) {
                return 'past-flight';
              } else {
                if (flightToday && (takeOff[0] < date.getHours() || (takeOff[0] === date.getHours() && takeOff[1] < date.getMinutes()))) {
                  return 'in-flight';
                } else {
                  if (!data.hasBeenRead) {
                    return 'updated-flight';
                  } else {
                    return '';
                  }
                }
              }
            }
          } else {
            return 'past-flight';
          }
        }
      }
    }
    return '';
  };

  useEffect(() => {
    if (data.length > 0) {
      // switch with loadingState
      const headersId = headers.map(e => e.accessor);
      const visibles = visibleColumns.map(e => e.id);
      const hiddens = headersId.filter(v => !visibles.includes(v));
      saveToLocalStorage(name, hiddens);
    }
  }, [visibleColumns]);

  const IndeterminateCheckbox = React.forwardRef(({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <input type="checkbox" ref={resolvedRef} {...rest} />;
  });

  return (
    <div className="table">
      <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
        {filters && (
          <div className="table__filter" ref={filterRef}>
            <div className="table__toggle-filter" onClick={() => setFilterPopupVisibility(!filterPopupVisibility)}>
              <DisplayColumnIcon />
              FILTER
            </div>
            {filterPopupVisibility && (
              <div className="options">
                {allColumns
                  .filter(r => r.Header !== 'raw')
                  .map(
                    column =>
                      column.id !== 'selection' && (
                        <label key={column.id}>
                          <input type="checkbox" {...column.getToggleHiddenProps()} />
                          <span>{column.Header}</span>
                        </label>
                      ),
                  )}
              </div>
            )}
          </div>
        )}
        {/* Bouton Missions */}
        {allMissions.length > 0 && ["allFlightsFilters","flightsFilters","canceledFlightFilters"].includes(name) && (
          <div className="table__filter" ref={missionsRef}>
            <div className="table__toggle-filter" onClick={() => setMissionsPopupVisibility(!missionsPopupVisibility)}>
              <DisplayColumnIcon />
              MISSIONS
            </div>
            {missionsPopupVisibility && (
              <div className="options">
                {/* Case à cocher toutes les missions */}
                <label key="all-missions">
                  <input
                    type="checkbox"
                    checked={selectedMissions.length === allMissions.length}
                    indeterminate={selectedMissions.length > 0 && selectedMissions.length < allMissions.length}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedMissions(allMissions);
                      } else {
                        setSelectedMissions([]);
                      }
                    }}
                  />
                  <span>All</span>
                </label>
                {allMissions.map(mission => (
                  <label key={mission}>
                    <input
                      type="checkbox"
                      checked={selectedMissions.includes(mission)}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedMissions(prev => Array.from(new Set([...prev, mission])));
                        } else {
                          setSelectedMissions(prev => prev.filter(m => m !== mission));
                        }
                      }}
                    />
                    <span>{mission}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <table {...getTableProps()} ref={tableRef}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr key={v1()}>
              {headerGroup.headers
                .filter(r => r.Header !== 'raw')
                .map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>{column.render('Header')}</th>
                ))}
              {editable || onDelete || extraActions.length > 0 ? (
                <th className="table__actions">Actions</th>
              ) : null}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} className={applyRowClass(row)}>
                {row.cells
                  .filter(r => r.column.Header !== 'raw')
                  .map(cell => (
                    <td className={`${row.values?.raw?.passengerCapacity <= 5 && user?.user?.roleName == 'ASO' ? 'italic' : ''}`} {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                {editable || onDelete || extraActions.length > 0 ? (
                  <td className="icon table__actions">
                    {editable && (
                      <span title="Edit" onClick={() => onEdit(row.values)}>
                        <EditIcon fill="#000" />
                      </span>
                    )}
                    {extraActions.map(action => (
                      <span
                        key={action.title}
                        title={action.title}
                        onClick={() => action.onClick(row.values)}
                      >
                        {action.icon}
                      </span>
                    ))}
                    {onDelete && (
                      <span title="Delete" onClick={() => onDelete(row.values)}>
                        <DeleteIcon width={16} height={19} color="#c62828" fill="#c62828" />
                      </span>
                    )}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="table__footer">
        <span>{data.length} rows</span>
        {data.length > pageSize && (
          <span className="table__footer-pagination">
            <Button text="<" disabled={!canPreviousPage} defaultStyle={`primary less_padding${!canPreviousPage ? ' disabled' : ''}`} onClick={previousPage} />
            <span>
              Page {pageIndex + 1} of {pageOptions.length}
            </span>
            <Button text=">" disabled={!canNextPage} defaultStyle={`primary less_padding${!canNextPage ? ' disabled' : ''}`} onClick={nextPage} />
          </span>
        )}
        <span>
          <Select
            options={[10, 20, 30, 40, 50].map(v => ({ value: v, label: `Show ${v}` }))}
            itemValue="value"
            itemLabel="label"
            onChange={value => setPageSize(+value)}
            value={pageSize}
          />
        </span>
      </div>
    </div>
  );
};

export default Table;
